import React, { useState, useEffect, useRef } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { ThemeProvider } from './ThemeProvider'
import { ThemeMenu } from './ThemeMenu'
import { TermsAcceptanceModal } from './TermsAcceptanceModal'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import type { PageProps } from '@/types'
import {
  LayoutDashboard, Trophy, Wallet, Medal, Bookmark,
  Bell, LogOut, Menu, ChevronRight, Flame, Target, ClipboardList,
  PanelLeftClose, PanelLeftOpen, Coins, Home, GraduationCap, User,
  Settings, HelpCircle, X, Globe, Newspaper
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface NavItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number | string
}

function formatRemainingMinutes(seconds: number): string {
  if (seconds <= 0) return 'Time up'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) {
    return `${hours}h ${minutes}m left`
  }
  return `${minutes}m left`
}

export function StudentShell({ children }: { children: React.ReactNode }) {
  const pageProps = usePage<PageProps>().props
  const { auth, flash, unread_notifications_count, ongoing_attempt } = pageProps
  const { toast } = useToast()
  const lastFlashRef = useRef<{ notice?: string; alert?: string }>({})
  const creditBalance = ((pageProps as Record<string, unknown>).credit_balance as number | undefined) ?? 0
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [ongoingSeconds, setOngoingSeconds] = useState<number>(() => ongoing_attempt?.remaining_seconds || 0)
  const [sectionSeconds, setSectionSeconds] = useState<number | null>(() => ongoing_attempt?.active_section_remaining_seconds ?? null)
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('prepkiya_sidebar_collapsed') === 'true'
    }
    return false
  })

  useEffect(() => {
    if (ongoing_attempt?.remaining_seconds != null) {
      setOngoingSeconds(ongoing_attempt.remaining_seconds)
    }
    if (ongoing_attempt?.active_section_remaining_seconds != null) {
      setSectionSeconds(ongoing_attempt.active_section_remaining_seconds)
    } else {
      setSectionSeconds(null)
    }
  }, [ongoing_attempt?.remaining_seconds, ongoing_attempt?.active_section_remaining_seconds, ongoing_attempt?.id])

  // Ensure window/body does not scroll when inside student shell
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlHeight = document.documentElement.style.height
    const originalBodyHeight = document.body.style.height

    document.documentElement.style.overflow = 'hidden'
    document.documentElement.style.height = '100%'
    document.body.style.overflow = 'hidden'
    document.body.style.height = '100%'

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow
      document.documentElement.style.height = originalHtmlHeight
      document.body.style.overflow = originalBodyOverflow
      document.body.style.height = originalBodyHeight
    }
  }, [])

  useEffect(() => {
    if (!ongoing_attempt || ongoingSeconds <= 0) return

    const timer = setInterval(() => {
      setOngoingSeconds((prev) => Math.max(0, prev - 1))
      setSectionSeconds((prev) => (prev != null ? Math.max(0, prev - 1) : null))
    }, 1000)

    return () => clearInterval(timer)
  }, [ongoing_attempt, ongoingSeconds])

  useEffect(() => {
    localStorage.setItem('prepkiya_sidebar_collapsed', String(collapsed))
  }, [collapsed])

  useEffect(() => {
    if (flash?.notice && flash.notice !== lastFlashRef.current.notice) {
      toast({ title: 'Success', description: flash.notice })
      lastFlashRef.current.notice = flash.notice
    }
    if (flash?.alert && flash.alert !== lastFlashRef.current.alert) {
      toast({ title: 'Alert', description: flash.alert, variant: 'destructive' })
      lastFlashRef.current.alert = flash.alert
    }
  }, [flash, toast])

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

  const navItems: NavItem[] = [
    { label: 'Dashboard',    href: '/student',             icon: LayoutDashboard },
    { label: 'My Exams',     href: '/student/exams',       icon: Target },
    { label: 'Tests',        href: '/student/tests',       icon: ClipboardList },
    { label: 'Leaderboard',  href: '/student/leaderboard', icon: Trophy },
    { label: 'Wallet',       href: '/student/credits',     icon: Wallet },
    { label: 'Badges',       href: '/student/badges',      icon: Medal },
    { label: 'Bookmarks',    href: '/student/bookmarks',   icon: Bookmark },
    { label: 'Notifications',href: '/student/notifications',icon: Bell,
      badge: unread_notifications_count && unread_notifications_count > 0 ? unread_notifications_count : undefined },
  ]

  // Drawer nav items matching Mockup Image 1
  const drawerNavItems: NavItem[] = [
    { label: 'Dashboard',    href: '/student',             icon: LayoutDashboard },
    { label: 'My Exams',     href: '/student/exams',       icon: Target },
    { label: 'Tests',        href: '/student/tests',       icon: ClipboardList },
    { label: 'Leaderboard',  href: '/student/leaderboard', icon: Trophy },
    { label: 'Wallet',       href: '/student/credits',     icon: Wallet },
    { label: 'Badges',       href: '/student/badges',      icon: Medal },
    { label: 'Bookmarks',    href: '/student/bookmarks',   icon: Bookmark },
    { label: 'Notifications',href: '/student/notifications',icon: Bell,
      badge: unread_notifications_count && unread_notifications_count > 0 ? unread_notifications_count : undefined },
    { label: 'Settings',     href: '/student/profile',     icon: Settings },
    { label: 'Blog',         href: '/blog',                icon: Newspaper },
    { label: 'Public Website',href: '/',                   icon: Globe },
    { label: 'Help & Support',href: '/help',                 icon: HelpCircle },
  ]

  // Bottom navigation items matching Mockup Image 2
  const bottomNavItems = [
    { label: 'Home',     href: '/student',             icon: Home },
    { label: 'Practice', href: '/student/tests',       icon: ClipboardList },
    { label: 'Exams',    href: '/student/exams',       icon: GraduationCap },
    { label: 'Rank',     href: '/student/leaderboard', icon: Trophy },
    { label: 'Profile',  href: '/student/profile',     icon: User },
  ]

  const user = auth.user
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'ST'

  // Desktop Sidebar Content
  const DesktopSidebarContent = ({ isCollapsed = false }: { isCollapsed?: boolean }) => (
    <div className="flex flex-col h-full bg-glass/85 backdrop-blur-glass border-r border-glass-border shadow-glass-md transition-all duration-300 select-none">
      {/* Header with Logo & Toggle Button */}
      <div className={`py-4 border-b border-border/40 flex items-center transition-all ${isCollapsed ? 'px-2 flex-col gap-3 justify-center' : 'px-4 justify-between'}`}>
        <Link href="/" title="PrepKiya Home" className="flex items-center gap-3 group min-w-0">
          <img src="/logo.svg" alt="PrepKiya Logo" className="w-9 h-9 rounded-xl object-cover shrink-0 transition-transform group-hover:scale-105" />
          {!isCollapsed && (
            <div className="flex flex-col min-w-0">
              <span className="text-lg font-extrabold text-foreground tracking-tight truncate">
                PrepKiya
              </span>
              <span className="text-[10px] text-muted-foreground font-medium tracking-tight truncate">
                Prepare, practice, perfect.
              </span>
            </div>
          )}
        </Link>

        {/* Desktop Collapse Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className={`hidden lg:flex text-muted-foreground hover:text-foreground hover:bg-muted/50 shrink-0 ${
            isCollapsed ? 'h-7 w-7' : 'h-8 w-8'
          }`}
          title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {isCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2.5 py-4 space-y-1.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = currentPath === item.href || (item.href !== '/student' && currentPath.startsWith(item.href))
          return (
            <Link
              key={item.href}
              href={item.href}
              title={isCollapsed ? item.label : undefined}
              className={`relative flex items-center rounded-xl text-xs sm:text-sm font-semibold transition-all group ${
                isCollapsed ? 'justify-center p-2.5' : 'px-3.5 py-2.5 gap-3'
              } ${
                isActive
                  ? 'bg-primary/15 text-primary border border-primary/30 shadow-glass-sm'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground border border-transparent'
              }`}
            >
              <Icon className={`h-4.5 w-4.5 transition-colors shrink-0 ${isActive ? 'text-primary' : 'group-hover:text-foreground'}`} />
              {!isCollapsed && <span className="flex-1 truncate">{item.label}</span>}
              {item.badge != null && (
                <Badge
                  variant="destructive"
                  className={`font-bold border-0 ${
                    isCollapsed
                      ? 'absolute -top-0.5 -right-0.5 h-4 min-w-4 p-0 text-[9px] flex items-center justify-center rounded-full shadow-xs'
                      : 'h-5 min-w-5 px-1.5 text-[10px]'
                  }`}
                >
                  {item.badge}
                </Badge>
              )}
              {isActive && !isCollapsed && <ChevronRight className="h-4 w-4 text-primary shrink-0" />}
            </Link>
          )
        })}
      </nav>

      {/* Sidebar Footer — User Profile Card & Sign Out */}
      {user && (
        <div className={`border-t border-border/40 transition-all ${isCollapsed ? 'p-2 flex flex-col items-center gap-2' : 'p-2.5'}`}>
          <div className={`flex items-center gap-2 ${isCollapsed ? 'flex-col' : 'justify-between'}`}>
            <Link
              href="/student/profile"
              title={isCollapsed ? `${user.name} - Profile` : undefined}
              className={`flex items-center gap-2.5 min-w-0 rounded-xl transition-all group flex-1 ${
                isCollapsed
                  ? 'p-1.5 justify-center hover:bg-muted/60'
                  : 'p-2 hover:bg-muted/50 border border-transparent hover:border-border/50'
              } ${
                currentPath.startsWith('/student/profile')
                  ? 'bg-primary/10 border-primary/30 text-primary'
                  : ''
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-primary text-primary-fg flex items-center justify-center font-extrabold text-xs shadow-xs shrink-0 overflow-hidden" title={user.name}>
                {user.avatar_base64 || user.avatar_url ? (
                  <img
                    src={user.avatar_base64 || user.avatar_url || ''}
                    alt={user.name}
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              {!isCollapsed && (
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-foreground truncate group-hover:text-primary transition-colors">{user.name}</p>
                  {user.streak_days != null && user.streak_days > 0 ? (
                    <p className="text-[11px] text-secondary font-semibold flex items-center gap-1 mt-0.5 truncate">
                      <Flame className="h-3.5 w-3.5 text-secondary fill-secondary shrink-0" /> {user.streak_days}d streak
                    </p>
                  ) : (
                    <p className="text-[10px] text-muted-foreground truncate">View Profile</p>
                  )}
                </div>
              )}
            </Link>

            <Link
              href="/users/sign_out"
              method="delete"
              as="button"
              title="Sign Out"
              className={`flex items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all shrink-0 ${
                isCollapsed ? 'w-8 h-8' : 'w-8 h-8'
              }`}
            >
              <LogOut className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <ThemeProvider initialThemeName={user?.theme_name} portal="student">
      <div className="app-shell-root fixed inset-0 flex h-full w-full overflow-hidden bg-background select-none">
        {/* Desktop Collapsible Sidebar */}
        <aside className={`hidden lg:flex lg:flex-col shrink-0 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}>
          <DesktopSidebarContent isCollapsed={collapsed} />
        </aside>

        {/* Mobile Slide-out Drawer (Mockup Image 1) */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="absolute left-0 top-0 h-full w-[84vw] max-w-[340px] z-50 bg-card rounded-r-3xl p-4 sm:p-5 flex flex-col justify-between shadow-glass-lg border-r border-border/80 transition-transform duration-300 ease-out animate-in slide-in-from-left">
              {/* Drawer Header: Logo + Brand + Close Button */}
              <div className="flex items-center justify-between pb-3.5 border-b border-border/40 shrink-0">
                <Link
                  href="/student"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 min-w-0"
                >
                  <div className="w-10 h-10 rounded-full bg-foreground text-background flex items-center justify-center font-black text-lg shadow-xs shrink-0">
                    P
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-base font-extrabold text-foreground tracking-tight truncate">
                      PrepKiya
                    </span>
                    <span className="text-[11px] text-muted-foreground font-medium truncate">
                      Prepare, practice, perfect.
                    </span>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="w-9 h-9 rounded-full border border-border/70 bg-card hover:bg-muted/40 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0 shadow-2xs"
                  aria-label="Close menu"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Drawer Navigation List */}
              <nav className="flex-1 py-2 overflow-y-auto min-h-0 divide-y divide-border/20">
                {drawerNavItems.map((item) => {
                  const Icon = item.icon
                  const isActive = currentPath === item.href || (item.href !== '/student' && currentPath.startsWith(item.href))
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center justify-between px-4 py-3 text-sm font-semibold transition-all ${
                        isActive
                          ? 'text-primary bg-primary/5 font-bold'
                          : 'text-foreground/80 hover:bg-muted/40 hover:text-foreground'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                          isActive ? 'bg-primary/15 text-primary border border-primary/25' : 'bg-muted/60 text-muted-foreground'
                        }`}>
                          <Icon className="h-4.5 w-4.5" />
                        </div>
                        <span className="truncate">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {item.badge != null && (
                          <span className="h-5 min-w-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-extrabold flex items-center justify-center shadow-2xs">
                            {item.badge}
                          </span>
                        )}
                        <ChevronRight className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground/60'}`} />
                      </div>
                    </Link>
                  )
                })}
              </nav>

              {/* Drawer Footer — Profile Card & Sign Out */}
              {user && (
                <div className="pt-3 border-t border-border/40 shrink-0">
                  <div className="p-2.5 rounded-2xl border border-border/70 bg-muted/20 flex items-center justify-between gap-2 shadow-2xs">
                    <Link
                      href="/student/profile"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center gap-2.5 min-w-0 flex-1 group"
                    >
                      <div className="w-9 h-9 rounded-full bg-primary text-primary-fg flex items-center justify-center font-extrabold text-xs shadow-xs shrink-0 overflow-hidden">
                        {user.avatar_base64 || user.avatar_url ? (
                          <img
                            src={user.avatar_base64 || user.avatar_url || ''}
                            alt={user.name}
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          initials
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-foreground truncate group-hover:text-primary transition-colors">{user.name}</p>
                        <p className="text-[10px] text-primary font-medium hover:underline">View Profile</p>
                      </div>
                    </Link>

                    <Link
                      href="/users/sign_out"
                      method="delete"
                      as="button"
                      title="Sign Out"
                      className="w-8 h-8 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors shrink-0"
                    >
                      <LogOut className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </aside>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          {/* Global Topbar */}
          <header className="flex items-center justify-between px-3 sm:px-6 h-14 border-b border-border/40 bg-card shrink-0 gap-2 sm:gap-3 shadow-2xs">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {/* Mobile Menu Button (Image 1 trigger) */}
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden h-9 w-9 flex items-center justify-center rounded-xl border border-border/70 bg-card hover:bg-muted/40 text-foreground transition-colors shadow-2xs shrink-0"
                aria-label="Open menu"
              >
                <Menu className="h-4.5 w-4.5 text-foreground" />
              </button>

              {/* Mobile Brand Logo + Text */}
              <Link href="/" className="lg:hidden flex items-center gap-2 font-extrabold text-foreground text-base" title="PrepKiya Home">
                <div className="w-7 h-7 rounded-full bg-foreground text-background flex items-center justify-center font-black text-xs shadow-xs shrink-0">
                  P
                </div>
                <span className="tracking-tight">PrepKiya</span>
              </Link>

              {/* Desktop Breadcrumb/Section */}
              <div className="hidden lg:flex items-center gap-2 min-w-0">
                <span className="text-xs font-bold text-muted-foreground">Student Portal</span>
                <span className="text-muted-foreground/40">/</span>
                <span className="text-xs font-bold text-foreground capitalize truncate">
                  {currentPath === '/student'
                    ? 'Dashboard'
                    : currentPath.split('/')[2]?.replace(/_/g, ' ') || 'Overview'}
                </span>
              </div>
            </div>

            {/* Topbar Right Controls */}
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              {/* Ongoing Test Attempt Indicator — Click to Auto-Resume */}
              {ongoing_attempt && (
                <Link
                  href={ongoing_attempt.resume_url}
                  title={`Ongoing test: ${ongoing_attempt.test_title}${ongoing_attempt.is_sectional && ongoing_attempt.active_section_name ? ` (${ongoing_attempt.active_section_name})` : ''} • ${formatRemainingMinutes(ongoing_attempt.is_sectional && sectionSeconds != null ? sectionSeconds : ongoingSeconds)} — Click to Resume`}
                  className="flex items-center gap-1.5 sm:gap-2 bg-tertiary/15 hover:bg-tertiary/25 border border-tertiary/30 px-2.5 sm:px-3 py-1 rounded-full text-xs font-bold text-tertiary transition-all shadow-xs group"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-tertiary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-tertiary"></span>
                  </span>
                  <span className="hidden sm:inline truncate max-w-[120px] md:max-w-[180px]">
                    {ongoing_attempt.test_title}
                  </span>
                  <span className="text-[11px] font-semibold opacity-90">
                    {formatRemainingMinutes(ongoing_attempt.is_sectional && sectionSeconds != null ? sectionSeconds : ongoingSeconds)}
                  </span>
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-primary group-hover:translate-x-0.5 transition-transform hidden xs:inline">
                    Resume →
                  </span>
                </Link>
              )}

              {/* Public Site Link */}
              <Link
                href="/"
                title="View Public Site"
                className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-border/50 transition-all shadow-xs"
              >
                <Globe className="h-3.5 w-3.5" />
                <span>Public Site</span>
              </Link>

              {/* Wallet Credits */}
              <Link
                href="/student/credits"
                title="Wallet Credits"
                className="hidden sm:flex items-center gap-1.5 bg-tertiary/10 border border-tertiary/25 px-2.5 py-1 rounded-full text-[11px] font-bold text-tertiary hover:bg-tertiary/20 transition-all shadow-xs"
              >
                <Coins className="h-3.5 w-3.5 text-tertiary" />
                <span>{creditBalance}</span>
              </Link>

              {/* Notifications Bell with Badge */}
              <Link
                href="/student/notifications"
                title="Notifications"
                className="relative p-2 text-muted-foreground hover:text-foreground rounded-xl hover:bg-muted/50 transition-colors"
              >
                <Bell className="h-4.5 w-4.5 text-foreground" />
                {unread_notifications_count != null && unread_notifications_count > 0 && (
                  <span className="absolute top-1 right-1 h-4 min-w-4 px-1 bg-destructive rounded-full text-[9px] text-destructive-foreground flex items-center justify-center font-extrabold shadow-2xs">
                    {unread_notifications_count}
                  </span>
                )}
              </Link>

              {/* Unified Theme & Mode Selector Menu */}
              <ThemeMenu />
            </div>
          </header>

          {/* Page View Container with bottom clearance for mobile 5-tab bar */}
          <main className="flex-1 min-h-0 overflow-y-auto min-w-0 pb-20 lg:pb-6">
            <div className="container max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 min-w-0 overflow-x-hidden">
              {children}
            </div>
          </main>
        </div>

        {/* Mobile Sticky 5-Tab Bottom Navigation Bar (< lg): Home | Practice | Exams | Rank | Profile */}
        <div className="fixed bottom-0 left-0 right-0 z-30 lg:hidden border-t border-border bg-card/95 backdrop-blur-md grid grid-cols-5 py-1 pb-[env(safe-area-inset-bottom,0px)] shadow-glass-md">
          {bottomNavItems.map(item => {
            const Icon = item.icon
            const isActive = item.href === '/student'
              ? currentPath === '/student'
              : currentPath.startsWith(item.href)

            return (
              <Link
                key={item.href}
                href={item.href}
                className={`relative flex flex-col items-center justify-center py-1.5 px-1 transition-all ${
                  isActive
                    ? 'text-primary font-bold'
                    : 'text-muted-foreground hover:text-foreground font-medium'
                }`}
              >
                <div className={`p-1.5 rounded-xl transition-all ${isActive ? 'bg-primary/10' : ''}`}>
                  <Icon className={`h-5 w-5 ${isActive ? 'stroke-[2.5]' : 'stroke-[1.75]'}`} />
                </div>
                <span className="text-[10px] tracking-tight leading-none">{item.label}</span>
                {isActive && (
                  <span className="absolute -bottom-px left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            )
          })}
        </div>
      </div>
      <TermsAcceptanceModal />
      <Toaster />
    </ThemeProvider>
  )
}


